import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Shield, CheckCircle2, XCircle, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "../../components/ui/button";
import api from "../../lib/api";
import { useAppConfig } from "../../lib/AppConfigContext";

export default function AdminConfirmDeletionPage() {
  const { t } = useTranslation();
  const { appName } = useAppConfig();
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [status, setStatus] = useState(null);
  const [tokenData, setTokenData] = useState(null);
  const [result, setResult] = useState(null);
  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;
    checkToken();
  }, [token]);

  const checkToken = async () => {
    try {
      const response = await api.get(`/admin/deletion-status/${token}`);
      if (response.data.valid) {
        setTokenData(response.data);
        setStatus("valid");
      } else {
        setStatus("invalid");
      }
    } catch {
      setStatus("invalid");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setProcessing(true);
    try {
      const response = await api.get(`/admin/confirm-deletion/${token}`);
      setResult({ success: true, message: response.data.message });
    } catch (error) {
      setResult({ success: false, message: error.response?.data?.detail || t('confirmDeletion.error') });
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = async () => {
    setProcessing(true);
    try {
      const response = await api.get(`/admin/cancel-deletion/${token}`);
      setResult({ cancelled: true, message: response.data.message });
    } catch (error) {
      setResult({ success: false, message: error.response?.data?.detail || t('confirmDeletion.error') });
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-12 h-12 bg-amber-600 rounded-xl flex items-center justify-center">
            <Shield className="h-6 w-6 text-white" />
          </div>
          <h1 className="font-heading font-bold text-xl text-white">{appName}</h1>
        </div>

        <div className="bg-slate-800 rounded-xl border border-slate-700 p-8">
          {result ? (
            <div className="text-center">
              {result.success ? (
                <>
                  <div className="w-16 h-16 bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="h-8 w-8 text-green-400" />
                  </div>
                  <h2 className="text-xl font-bold text-white mb-2">{t('confirmDeletion.completed')}</h2>
                </>
              ) : result.cancelled ? (
                <>
                  <div className="w-16 h-16 bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <XCircle className="h-8 w-8 text-blue-400" />
                  </div>
                  <h2 className="text-xl font-bold text-white mb-2">{t('confirmDeletion.cancelled')}</h2>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <XCircle className="h-8 w-8 text-red-400" />
                  </div>
                  <h2 className="text-xl font-bold text-white mb-2">{t('confirmDeletion.error')}</h2>
                </>
              )}
              <p className="text-slate-400 mb-6">{result.message}</p>
            </div>
          ) : status === "invalid" ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="h-8 w-8 text-red-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">{t('confirmDeletion.invalidLink')}</h2>
              <p className="text-slate-400 mb-6">{t('confirmDeletion.invalidLinkDesc')}</p>
            </div>
          ) : (
            <div className="text-center">
              <div className="w-16 h-16 bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="h-8 w-8 text-red-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">{t('confirmDeletion.title')}</h2>
              <p className="text-slate-400 mb-4">
                {t('confirmDeletion.aboutToDelete', {
                  type: tokenData?.item_type === "entity" ? t('confirmDeletion.entity') : t('confirmDeletion.consulta')
                })}
              </p>
              <div className="bg-slate-700/50 rounded-lg p-4 mb-4">
                <p className="text-white font-semibold">{tokenData?.item_name}</p>
              </div>
              <p className="text-red-400 text-sm mb-6">{t('confirmDeletion.irreversibleWarning')}</p>
              <div className="flex gap-3 justify-center">
                <Button
                  onClick={handleCancel}
                  disabled={processing}
                  variant="outline"
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  data-testid="deletion-cancel-btn"
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  onClick={handleConfirm}
                  disabled={processing}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="deletion-confirm-btn"
                >
                  {processing && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  {t('confirmDeletion.confirmBtn')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
